import axios from 'axios'
import { clearRulesEngineSession, getValidToken } from './rulesEngineSession'
import {
  createRulesEngineError,
  toRulesEngineError,
} from './rulesEngineErrors'
import {
  getRateLimitDelayMs,
  waitForRetryDelay,
} from './rulesEngineRateLimit'
import { clearUserAuthenticated } from '../helpers/authSession'
import { routePaths } from '../config/navigation'

const REQUEST_TIMEOUT_MS = 15000
const MAX_429_RETRIES = 3

const withAuthHeader = (headers, token) => ({
  ...(headers || {}),
  Authorization: `Bearer ${token}`,
})

const redirectToLoginAfterSessionLoss = () => {
  clearRulesEngineSession()
  clearUserAuthenticated()

  if (typeof window !== 'undefined' && window.location.pathname !== routePaths.login) {
    window.location.assign(routePaths.login)
  }
}

const performRulesEngineRequest = async (requestConfig, state) => {
  try {
    const token = await getValidToken()

    return await axios.request({
      ...requestConfig,
      headers: withAuthHeader(requestConfig.headers, token),
      timeout: requestConfig.timeout || REQUEST_TIMEOUT_MS,
    })
  } catch (err) {
    if (err?.isSessionExpired) {
      redirectToLoginAfterSessionLoss()

      throw createRulesEngineError({
        status: 401,
        cause: err,
      })
    }

    const status = err?.response?.status

    if (status === 401) {
      redirectToLoginAfterSessionLoss()

      throw createRulesEngineError({
        status: 401,
        cause: err,
      })
    }

    if (status === 429 && state.rateLimitRetries < MAX_429_RETRIES) {
      const nextRetryCount = state.rateLimitRetries + 1
      const delay = getRateLimitDelayMs(nextRetryCount)

      await waitForRetryDelay(delay)

      return performRulesEngineRequest(requestConfig, {
        ...state,
        rateLimitRetries: nextRetryCount,
      })
    }

    if (status === 403) {
      throw createRulesEngineError({
        status: 403,
        cause: err,
      })
    }

    throw toRulesEngineError(err, { status })
  }
}

export const rulesEngineRequest = async ({
  method = 'get',
  url,
  params,
  data,
  headers,
  timeout,
}) => {
  if (!url) {
    throw createRulesEngineError({
      message: 'Rules engine request URL is required.',
    })
  }

  return performRulesEngineRequest({
    method,
    url,
    params,
    data,
    headers,
    timeout,
  }, {
    rateLimitRetries: 0,
  })
}

export const rulesEngineGet = async (url, config = {}) => {
  const response = await rulesEngineRequest({
    ...config,
    method: 'get',
    url,
  })

  return response?.data
}
